import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import Errors from '../common/Errors';
import { DividedRow, Half } from '../common/RowLengths';
import Select from '../common/Select';
import { MarkedEditor } from '../common/Marked';
import {
  handleFieldsetCreate,
  generateAutoEmailFieldset,
} from './constants/handleFieldset';
import { sortable } from '../../helpers/sorters';
import Icon from '../common/Icon';

const NewFieldset = ({
  setFieldsets,
  sortValue,
  formID,
  setCreating,
  canGenerateAutoEmailCaptureFieldset,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: '',
      comment: '',
      description: '',
      conditional_text: '',
      conditional_type: null,
      title_hidden: false,
      public: false,
      sort: sortValue,
    },
    criteriaMode: 'all',
  });

  useEffect(() => {
    document.getElementById('fieldset-name').focus();
  }, []);

  useEffect(() => {
    if (watch('conditional_text') === '') {
      setValue('conditional_type', null, { shouldDirty: true });
    } else if (!watch('conditional_type')) {
      setValue('conditional_type', 'checkbox', { shouldDirty: true });
    }
  }, [watch('conditional_text')]);

  const onSubmit = async (data) => {
    const fieldsetData = {
      ...data,
      form_id: formID,
      sort: sortValue,
      public: !data.public,
    };
    let fieldset = await handleFieldsetCreate(fieldsetData);
    if (fieldset.ok) {
      setFieldsets((fs) => sortable([...fs, fieldset.data]));
      setCreating(false);
    } else if (fieldset.errors) {
      Object.entries(fieldset.errors).forEach(([key, message]) =>
        setError(key, { types: { [key]: message } })
      );
    }
  };

  const handleGenerateAutoEmailCapture = async () => {
    const { fieldset } = await generateAutoEmailFieldset(formID, sortValue);
    if (fieldset.ok) {
      setFieldsets((fs) => sortable([...fs, fieldset.data]));
      setCreating(false);
    }
  };

  return (
    <>
      <header>
        <h3>Create Fieldset</h3>
      </header>

      <div className='creation-form'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <fieldset>
            <div className='row'>
              <label htmlFor='fieldset-name'>Name:</label>
              <input
                id='fieldset-name'
                placeholder='Fieldset name...'
                {...register('name', { required: 'Required' })}
              />
              <ErrorMessage
                errors={errors}
                name='name'
                render={({ messages }) => <Errors messages={messages} />}
              />
            </div>

            <div className='row'>
              <label htmlFor='fieldset-comment'>Comment:</label>
              <input
                id='fieldset-comment'
                placeholder='What is this for? (will not be displayed on form)'
                {...register('comment')}
              />
            </div>

            <div className='row'>
              <label>Description:</label>
              <MarkedEditor
                register={{ ...register('description') }}
                getValues={() => getValues('description')}
              />
            </div>
          </fieldset>

          <div className='row'>
            <label htmlFor='conditional-text'>Conditional Text:</label>
            <input
              id='conditional-text'
              placeholder='Enter prompt for conditional fieldset'
              {...register('conditional_text')}
            />
          </div>

          {watch('conditional_text') && (
            <div className='row'>
              <label>Conditional Type:</label>
              <Select
                content={[
                  { value: 'checkbox', label: 'Checkbox' },
                  { value: 'radio', label: 'Radio' },
                ]}
                register={{
                  ...register('conditional_type', { shouldUnregister: true }),
                }}
              />
            </div>
          )}

          <DividedRow>
            <Half hasCheckbox title='Private (Admin Only)' labelFor='admin-fs'>
              <input type='checkbox' id='admin-fs' {...register('public')} />
            </Half>
            <Half hasCheckbox title='Hide Fieldset Title' labelFor='hidden-fs'>
              <input
                type='checkbox'
                id='hidden-fs'
                {...register('title_hidden')}
              />
            </Half>
          </DividedRow>

          <div className='controls'>
            <button className='single' type='submit'>
              Save
              <Icon icon='save' />
            </button>
            {canGenerateAutoEmailCaptureFieldset && (
              <button
                className='right'
                onClick={() => handleGenerateAutoEmailCapture()}
                type='button'
              >
                Generate Parent Email [Auto]
              </button>
            )}
          </div>
        </form>
      </div>
    </>
  );
};

export default NewFieldset;
